import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  X, Loader2, Trash2, User, EyeOff, Merge, Clock, Archive, RotateCcw, Scissors,
} from 'lucide-react'
import { formatRelativeTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'
import { MergePersonDialog } from '@/components/MergePersonDialog'
import { SplitPersonDrawer } from '@/components/SplitPersonDrawer'
import { DetectionShot, type Detection, type ShotView } from '@/components/DetectionShot'
import type { Person, Classification } from '@/types'

interface PersonDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  personId: string | null
  initialPerson?: Person | null
  groupId: string
  onUpdated: () => void
  onDeleted: () => void
  onNavigateToSightings?: (personId: string, sightingId?: string, personName?: string) => void
  canManagePersons?: boolean
  isAdmin?: boolean
  isArchiveView?: boolean
}

/**
 * The person record, as a review panel.
 *
 * Follows the merge-compare drawer rather than a centred dialog: judging a
 * person means looking at their detections, and a square face crop rarely
 * settles anything. Same shots as the comparison — the person cropped out of
 * the source frame, with the face boxed — so the same evidence reads the same
 * way wherever you meet it.
 */
export function PersonDetailDialog({
  open,
  onOpenChange,
  personId,
  initialPerson,
  groupId,
  onUpdated,
  onDeleted,
  canManagePersons = true,
  isAdmin = false,
  isArchiveView = false,
}: PersonDetailDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [person, setPerson] = useState<Person | null>(null)
  const [classifications, setClassifications] = useState<Classification[]>([])
  const [detections, setDetections] = useState<Detection[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false)
  const [splitDrawerOpen, setSplitDrawerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<ShotView>('person')

  // Form state
  const [name, setName] = useState('')
  const [classificationId, setClassificationId] = useState('')
  const [notes, setNotes] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const applyPerson = (p: Person) => {
    setPerson(p)
    setName(p.name || '')
    setClassificationId(p.classificationId)
    setNotes(p.notes || '')
    setExpiresAt(p.expiresAt ? p.expiresAt.split('T')[0] : '')
  }

  const loadData = useCallback(async () => {
    if (!personId || !groupId) return

    setLoading(true)
    setError(null)

    try {
      const getClassificationsFn = apiCallable('getClassifications')
      const classResult = await getClassificationsFn({ groupId })
      const classData = classResult.data as { success: boolean; classifications: Classification[] }
      if (classData.success) setClassifications(classData.classifications)

      if (initialPerson) {
        applyPerson(initialPerson)
      } else {
        const getPersonsFn = apiCallable('getPersons')
        const personsResult = await getPersonsFn({ groupId, personId })
        const personsData = personsResult.data as { success: boolean; persons: Person[] }
        if (personsData.success && personsData.persons.length > 0) {
          applyPerson(personsData.persons[0])
        } else {
          setError(t('persons.personNotFound'))
        }
      }

      // The person's full sighting history, from the durable person_sightings
      // index. NOT listPersonDetections: that reads face_events, which expire
      // after 90 days and would empty this grid for a dormant person while all
      // their sightings are still on file. The split review keeps using it,
      // because only detections can be selected and moved.
      const fn = apiCallable('listPersonSightings')
      const res = await fn({ groupId, personId, limit: 12 })
      const data = res.data as { detections: Detection[]; nextCursor: string | null }
      setDetections(data.detections || [])
      setCursor(data.nextCursor)
    } catch (err) {
      console.error('Failed to load person data:', err)
      setError(t('personDetail.failedToLoad'))
    } finally {
      setLoading(false)
    }
  }, [personId, groupId, initialPerson, t])

  const loadMore = async () => {
    if (!cursor || !personId) return
    setLoadingMore(true)
    try {
      const fn = apiCallable('listPersonSightings')
      const res = await fn({ groupId, personId, limit: 12, cursor })
      const data = res.data as { detections: Detection[]; nextCursor: string | null }
      setDetections((prev) => [...prev, ...(data.detections || [])])
      setCursor(data.nextCursor)
    } catch (err) {
      console.error('Failed to load more detections:', err)
      setError(t('personDetail.failedToLoad'))
    } finally {
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    if (open && personId) {
      loadData()
    } else {
      setPerson(null)
      setDetections([])
      setCursor(null)
      setName('')
      setClassificationId('')
      setNotes('')
      setExpiresAt('')
      setError(null)
    }
  }, [open, personId, loadData])

  const handleSave = async () => {
    if (!person) return
    setSaving(true)
    try {
      const updateFn = apiCallable('updatePerson')
      const payload: Record<string, any> = { groupId, personId: person.id }
      // Only send fields the user has permission to change
      if (canManagePersons) {
        payload.name = name || null
        payload.notes = notes || null
        payload.expiresAt = expiresAt || null
        payload.classificationId = classificationId
      }
      await updateFn(payload)
      onUpdated()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save person:', err)
      setError(t('personDetail.failedToSave'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!person) return
    setDeleting(true)
    try {
      const deleteFn = apiCallable('deletePerson')
      await deleteFn({ groupId, personId: person.id })
      onDeleted()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete person:', err)
      setError(t('personDetail.failedToDelete'))
    } finally {
      setDeleting(false)
    }
  }

  const handleArchive = async () => {
    if (!person) return
    setArchiving(true)
    try {
      const updateFn = apiCallable('updatePerson')
      await updateFn({ groupId, personId: person.id, archivedAt: new Date().toISOString() })
      onDeleted()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to archive person:', err)
      setError(t('personDetail.failedToArchive'))
    } finally {
      setArchiving(false)
    }
  }

  const handleRestore = async () => {
    if (!person) return
    setArchiving(true)
    try {
      const updateFn = apiCallable('updatePerson')
      await updateFn({ groupId, personId: person.id, archivedAt: null })
      onUpdated()
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to restore person:', err)
      setError(t('personDetail.failedToRestore'))
    } finally {
      setArchiving(false)
    }
  }

  const openSighting = (detection: Detection) => {
    if (!personId) return
    const query = `?person=${personId}&personName=${encodeURIComponent(person?.name || 'Unknown')}`
    if (detection.sightingId) {
      // sightingId may be a sighting sk (contains '#'); encode for the path.
      navigate(`/sightings/${encodeURIComponent(detection.sightingId)}${query}`)
    } else {
      navigate(`/sightings${query}`)
    }
  }

  if (!personId) return null

  const expiryDate = expiresAt ? new Date(expiresAt) : null
  const expired = !!expiryDate && expiryDate < new Date()

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-ink/55 backdrop-blur-[4px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed z-[60] flex flex-col bg-[rgba(246,241,230,0.98)] shadow-auvers-modal',
            // Mobile: bottom sheet
            'inset-x-0 bottom-0 max-h-[88vh] rounded-t-2xl border-t border-white/70',
            'pb-[env(safe-area-inset-bottom)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
            // Desktop ≥sm: right side panel, full height
            'sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:h-full sm:w-[560px] sm:max-h-none',
            'lg:w-[680px]',
            'sm:rounded-none sm:rounded-l-2xl sm:border-l sm:border-t-0',
            'sm:data-[state=closed]:slide-out-to-right sm:data-[state=open]:slide-in-from-right'
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-serif text-[20px] font-medium leading-tight text-ink">
                {t('personDetail.title')}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-1 text-[12px] text-ink-soft">
                {t('personDetail.description')}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md border border-ink/15 text-ink transition hover:bg-ink/5"
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Identity */}
          {person && (
            <div className="border-b border-rule px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-lg border border-rule bg-ink/5">
                  {person.thumbnailUrl ? (
                    <img src={person.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-5 w-5 text-ink-soft" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-[16px] font-medium leading-tight text-ink">
                    {person.name || t('common.unknown')}
                  </p>
                  <p className="truncate text-[11px] text-ink-soft">
                    {person.classificationName || '—'} ·{' '}
                    {t('sightings.count', { count: person.totalSightings })}
                  </p>
                </div>
              </div>
              <p className="mt-1.5 truncate text-[11px] text-ink-soft">
                {t('mergeSuggestions.compare.seenRange', {
                  first: formatRelativeTime(person.firstSeenAt, '—'),
                  last: formatRelativeTime(person.lastSeenAt, '—'),
                })}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                {expiryDate && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-2 py-1',
                      expired ? 'bg-[#7a3325] text-paper' : 'bg-paper-2 text-ink-soft'
                    )}
                  >
                    <Clock className="h-3 w-3" />
                    {expired ? t('personDetail.expired') : t('personDetail.expires')}:{' '}
                    {expiryDate.toLocaleDateString()}
                  </span>
                )}
                {person.archivedAt && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-paper-2 px-2 py-1 text-ink-soft">
                    <Archive className="h-3 w-3" />
                    {t('personDetail.archived')}: {new Date(person.archivedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center py-12 text-ink-soft">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : !person ? (
              <p className="py-8 text-center text-[13px] text-ink-soft">
                {t('persons.personNotFound')}
              </p>
            ) : (
              <>
                {!canManagePersons && (
                  <Alert className="mb-4">
                    <EyeOff className="h-4 w-4" />
                    <AlertDescription>{t('personDetail.viewOnlyNotice')}</AlertDescription>
                  </Alert>
                )}

                {/* Record fields */}
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-name" className="text-[11px] text-ink-soft">
                        {t('personDetail.name')}
                      </Label>
                      {canManagePersons ? (
                        <Input
                          id="edit-name"
                          placeholder={t('personDetail.namePlaceholder')}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      ) : (
                        <p className="text-[13px] text-ink">{name || t('common.unknown')}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-classification" className="text-[11px] text-ink-soft">
                        {t('personDetail.classification')}
                      </Label>
                      {canManagePersons ? (
                        <Select value={classificationId} onValueChange={setClassificationId}>
                          <SelectTrigger id="edit-classification">
                            <SelectValue placeholder={t('personDetail.selectClassification')} />
                          </SelectTrigger>
                          <SelectContent>
                            {classifications.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-3 w-3 rounded-full"
                                    style={{ backgroundColor: c.color }}
                                  />
                                  {c.displayName}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="flex items-center gap-2">
                          {person.classificationColor && (
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: person.classificationColor }}
                            />
                          )}
                          <span className="text-[13px]">
                            {person.classificationName || t('common.unknown')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(canManagePersons || notes) && (
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-notes" className="text-[11px] text-ink-soft">
                        {t('personDetail.notes')}
                      </Label>
                      {canManagePersons ? (
                        <Textarea
                          id="edit-notes"
                          placeholder={t('personDetail.notesPlaceholder')}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          rows={2}
                        />
                      ) : (
                        <p className="text-[13px] text-ink-soft">{notes}</p>
                      )}
                    </div>
                  )}

                  {canManagePersons && (
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-expires" className="text-[11px] text-ink-soft">
                        {t('personDetail.expirationDate')}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="edit-expires"
                          type="date"
                          value={expiresAt}
                          onChange={(e) => setExpiresAt(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="flex-1"
                        />
                        {expiresAt && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpiresAt('')}
                            className="text-ink-soft"
                          >
                            {t('common.remove')}
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-ink-soft">
                        {t('personDetail.expirationHint')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Detections */}
                <div className="mt-5 border-t border-rule pt-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <Label className="text-[11px] text-ink-soft">
                      {t('personDetail.recentSightings')}
                    </Label>
                    <div className="flex shrink-0 gap-1 rounded-md border border-rule p-0.5">
                      {(['person', 'frame'] as ShotView[]).map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setView(v)}
                          className={cn(
                            'rounded-sm px-2 py-1 text-[11px] font-medium transition',
                            view === v ? 'bg-ink text-paper' : 'text-ink-soft hover:bg-paper-2'
                          )}
                        >
                          {v === 'person'
                            ? t('mergeSuggestions.compare.viewPerson')
                            : t('mergeSuggestions.compare.viewFrame')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {detections.length === 0 ? (
                    <p className="py-6 text-center text-[12px] text-ink-soft">
                      {t('mergeSuggestions.compare.noSamples')}
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                        {detections.map((d, idx) => (
                          <DetectionShot
                            key={d.eventId || `d-${idx}`}
                            detection={d}
                            view={view}
                            onClick={() => openSighting(d)}
                          />
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-center gap-2">
                        {cursor && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={loadMore}
                            disabled={loadingMore}
                          >
                            {loadingMore && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            {t('split.loadMore')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigate(
                              `/sightings?person=${personId}&personName=${encodeURIComponent(person.name || 'Unknown')}`
                            )
                          }}
                        >
                          {t('personDetail.viewAllSightings')}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          {person && (
            <div className="border-t border-rule px-5 py-3">
              {canManagePersons && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {isArchiveView && isAdmin ? (
                    <>
                      {person.archivedAt && (
                        <Button variant="outline" size="sm" onClick={handleRestore} disabled={archiving}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                          {archiving ? t('common.saving') : t('personDetail.restorePerson')}
                        </Button>
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="destructive" size="sm">
                            <Trash2 className="mr-1 h-3.5 w-3.5" />
                            {t('personDetail.deletePerson')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('personDetail.deleteConfirmTitle')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('personDetail.deleteConfirmDescription')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={handleDelete}
                              disabled={deleting}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {deleting ? t('common.deleting') : t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setMergeDialogOpen(true)}>
                        <Merge className="mr-1 h-3.5 w-3.5" />
                        {t('personDetail.merge')}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setSplitDrawerOpen(true)}>
                        <Scissors className="mr-1 h-3.5 w-3.5" />
                        {t('personDetail.split')}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            <Archive className="mr-1 h-3.5 w-3.5" />
                            {t('personDetail.archive')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('personDetail.archiveConfirmTitle')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('personDetail.archiveConfirmDescription')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction onClick={handleArchive} disabled={archiving}>
                              {archiving ? t('common.saving') : t('personDetail.archivePerson')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                {canManagePersons ? (
                  <>
                    <Button className="flex-1" onClick={handleSave} disabled={saving || loading}>
                      {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                      {saving ? t('common.saving') : t('common.saveChanges')}
                    </Button>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                      {t('common.cancel')}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
                    {t('common.close')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Stacked above this panel — see SplitPersonDrawer's z-index note. */}
      {!isArchiveView && person && (
        <>
          <MergePersonDialog
            open={mergeDialogOpen}
            onOpenChange={setMergeDialogOpen}
            sourcePerson={person}
            groupId={groupId}
            onMerged={() => {
              onDeleted()
              onOpenChange(false)
            }}
          />
          <SplitPersonDrawer
            open={splitDrawerOpen}
            onOpenChange={setSplitDrawerOpen}
            groupId={groupId}
            personId={personId}
            onSplit={() => {
              // Counts, thumbnail and detections all moved — refresh both this
              // panel and the list behind it.
              onUpdated()
              loadData()
            }}
          />
        </>
      )}
    </DialogPrimitive.Root>
  )
}
